import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Download, Sparkles, Mail, RefreshCw, X, CheckSquare, Square, ChevronUp, ChevronDown, Wand2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

export default function BoardPacketModal({ projectId, projectName, onClose }) {
  const [rows, setRows] = useState([]); // ordered [{key,title,drafted,selected}]
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState(false);
  const [draftMsg, setDraftMsg] = useState('');
  const [polishing, setPolishing] = useState(false);
  const [polishMsg, setPolishMsg] = useState('');
  const [includeCover, setIncludeCover] = useState(false);
  const [cover, setCover] = useState('');
  const [genCover, setGenCover] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [sending, setSending] = useState(false);

  const loadSections = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/admin/nonprofit-accelerator/projects/${projectId}/packet-sections`);
      const secs = r.data.sections || [];
      setRows((prev) => {
        if (prev.length) return prev.map((p) => ({ ...p, drafted: (secs.find((s) => s.key === p.key) || {}).drafted }));
        return secs.map((s) => ({ ...s, selected: true }));
      });
      if (r.data.cover_letter) { setCover(r.data.cover_letter); setIncludeCover(true); }
    } catch (e) { toast.error('Could not load packet sections'); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadSections(); /* eslint-disable-next-line */ }, [projectId]);

  const orderedSelected = () => rows.filter((r) => r.selected).map((r) => r.key);
  const missingSelected = rows.filter((r) => r.selected && !r.drafted);

  const toggle = (key) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, selected: !r.selected } : r)));
  const move = (idx, dir) => setRows((rs) => {
    const j = idx + dir; if (j < 0 || j >= rs.length) return rs;
    const copy = [...rs]; const t = copy[idx]; copy[idx] = copy[j]; copy[j] = t; return copy;
  });

  const pollJob = (jobId, onProgress, onDone, onErr) => {
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const jr = await api.get(`/api/admin/nonprofit-accelerator/draft-jobs/${jobId}`);
        const d = jr.data;
        onProgress(d);
        if (d.status === 'done') onDone(d);
        else if (d.status === 'error') onErr(d.error || 'unknown');
        else if (attempts > 2000) onErr('timeout');
        else setTimeout(tick, 3000);
      } catch { if (attempts > 2000) onErr('network'); else setTimeout(tick, 4000); }
    };
    setTimeout(tick, 2000);
  };

  const download = async () => {
    const keys = orderedSelected();
    if (!keys.length) { toast.error('Pick at least one section'); return; }
    toast.info('Preparing board packet…');
    try {
      const r = await api.get(`/api/admin/nonprofit-accelerator/projects/${projectId}/board-packet-pdf?sections=${keys.join(',')}&cover=${includeCover ? 1 : 0}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${(projectName || 'nonprofit').replace(/\s+/g, '_')}_board_packet.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  const draftMissing = async () => {
    setDrafting(true); setDraftMsg('Starting…');
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${projectId}/draft-packet-missing`);
      if (!r.data.total) { setDrafting(false); toast.success('All selected pieces are already drafted.'); return; }
      pollJob(r.data.job_id,
        (d) => setDraftMsg(`Drafting ${d.completed || 0}/${d.total || 0}${d.current_title ? ' · ' + d.current_title : ''}…`),
        async () => { setDrafting(false); setDraftMsg(''); await loadSections(); toast.success('Missing pieces drafted.'); },
        (err) => { setDrafting(false); setDraftMsg(''); toast.error('Draft failed: ' + err); });
    } catch (e) { setDrafting(false); toast.error('Could not start: ' + (e.response?.data?.detail || e.message)); }
  };

  const polish = async () => {
    setPolishing(true); setPolishMsg('Starting…');
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${projectId}/polish-packet`, { sections: orderedSelected() });
      if (!r.data.total) { setPolishing(false); toast.info('Nothing drafted yet to polish. AI-draft the pieces first.'); return; }
      pollJob(r.data.job_id,
        (d) => setPolishMsg(`Polishing ${d.completed || 0}/${d.total || 0}${d.current_title ? ' · ' + d.current_title : ''}…`),
        async () => { setPolishing(false); setPolishMsg(''); await loadSections(); toast.success('Polished into one board-ready voice. Previous versions are saved in each day\'s history.'); },
        (err) => { setPolishing(false); setPolishMsg(''); toast.error('Polish failed: ' + err); });
    } catch (e) { setPolishing(false); toast.error('Could not start: ' + (e.response?.data?.detail || e.message)); }
  };

  const generateCover = async () => {
    setGenCover(true);
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${projectId}/packet-cover-letter`, {});
      setCover(r.data.cover_letter || ''); setIncludeCover(true);
      toast.success('Cover letter drafted - edit it if you like');
    } catch (e) { toast.error('Could not generate cover letter'); }
    finally { setGenCover(false); }
  };
  const saveCover = async () => {
    try { await api.post(`/api/admin/nonprofit-accelerator/projects/${projectId}/packet-cover-letter`, { text: cover }); } catch (_) {}
  };

  const sendEmail = async () => {
    if (!emailTo.includes('@')) { toast.error('Enter a valid email'); return; }
    setSending(true);
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${projectId}/email-packet`, { to: emailTo, message: emailMsg, sections: orderedSelected(), cover: includeCover });
      if (r.data.sent) toast.success(`Board packet emailed to ${emailTo}`);
      else toast.warning(r.data.detail || 'Email is not configured; packet not delivered.');
    } catch (e) { toast.error('Email failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="npa-packet-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-black text-[#0A0F1C] flex items-center gap-2"><FileText size={18} className="text-[#7C3AED]" /> Board Packet</h3>
          <button type="button" onClick={onClose} data-testid="npa-packet-close" className="text-[#94A3B8] hover:text-[#0A0F1C]"><X size={18} /></button>
        </div>
        <p className="text-xs text-[#64748B] mb-3">Pick and reorder the deliverables. AI-draft missing ones, polish everything into one voice, then download or email.</p>

        {loading ? (
          <div className="flex justify-center py-8"><RefreshCw className="animate-spin text-[#1D4ED8]" size={22} /></div>
        ) : (
          <div className="space-y-1.5 mb-3" data-testid="npa-packet-sections">
            {rows.map((s, i) => (
              <div key={s.key} data-testid={`npa-packet-section-${s.key}`} className="flex items-center gap-2 px-2 py-2 rounded-lg border border-[#E2E8F0]">
                <div className="flex flex-col">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`npa-packet-up-${s.key}`} className="text-[#94A3B8] hover:text-[#1D4ED8] disabled:opacity-30"><ChevronUp size={14} /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1} data-testid={`npa-packet-down-${s.key}`} className="text-[#94A3B8] hover:text-[#1D4ED8] disabled:opacity-30"><ChevronDown size={14} /></button>
                </div>
                <button type="button" onClick={() => toggle(s.key)} className="flex items-center gap-2 flex-1 text-left">
                  {s.selected ? <CheckSquare size={16} className="text-[#1D4ED8]" /> : <Square size={16} className="text-[#94A3B8]" />}
                  <span className="text-sm font-semibold text-[#0A0F1C] flex-1">{s.title}</span>
                </button>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${s.drafted ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>{s.drafted ? 'Ready' : 'Not drafted'}</span>
              </div>
            ))}
          </div>
        )}

        {missingSelected.length > 0 && (
          <button type="button" onClick={draftMissing} disabled={drafting} data-testid="npa-packet-draft-missing" className="w-full mb-2 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 rounded-lg hover:bg-fuchsia-100 disabled:opacity-60">
            {drafting ? <><RefreshCw size={13} className="animate-spin" /> {draftMsg || 'Drafting…'}</> : <><Sparkles size={13} /> AI-draft the {missingSelected.length} missing piece{missingSelected.length > 1 ? 's' : ''}</>}
          </button>
        )}

        <button type="button" onClick={polish} disabled={polishing} data-testid="npa-packet-polish" title="Rewrites the selected drafts into one consistent, board-ready voice (previous versions are saved)" className="w-full mb-3 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-60">
          {polishing ? <><RefreshCw size={13} className="animate-spin" /> {polishMsg || 'Polishing…'}</> : <><Wand2 size={13} /> AI polish pass (one consistent voice)</>}
        </button>

        {/* Cover letter */}
        <div className="border border-[#E2E8F0] rounded-lg p-3 mb-3" data-testid="npa-packet-cover">
          <label className="flex items-center gap-2 mb-2 cursor-pointer">
            <input type="checkbox" checked={includeCover} onChange={(e) => setIncludeCover(e.target.checked)} data-testid="npa-packet-cover-toggle" />
            <span className="text-sm font-bold text-[#0A0F1C]">Include a cover letter</span>
          </label>
          {includeCover && (
            <>
              <textarea value={cover} onChange={(e) => setCover(e.target.value)} onBlur={saveCover} data-testid="npa-packet-cover-text" rows={4} placeholder="Write a short intro, or generate one with AI…" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm mb-2" />
              <button type="button" onClick={generateCover} disabled={genCover} data-testid="npa-packet-cover-generate" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-[#7C3AED] text-[#7C3AED] rounded-lg hover:bg-[#F5F3FF] disabled:opacity-60">{genCover ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />} Generate with AI</button>
            </>
          )}
        </div>

        <button type="button" onClick={download} data-testid="npa-packet-download" className="w-full mb-4 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-bold bg-[#1D4ED8] text-white rounded-lg hover:opacity-90"><Download size={14} /> Download packet PDF</button>

        <div className="border-t border-[#E2E8F0] pt-3">
          <div className="flex items-center gap-2 mb-1"><Mail size={14} className="text-[#7C3AED]" /><span className="text-sm font-bold text-[#0A0F1C]">Email it to a board member</span></div>
          <input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="npa-packet-email-to" placeholder="board.member@example.com" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm mb-2" />
          <textarea value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} data-testid="npa-packet-email-msg" rows={2} placeholder="Optional note to include in the email…" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm mb-2" />
          <button type="button" onClick={sendEmail} disabled={sending} data-testid="npa-packet-email-send" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-[#0A0F1C] text-white rounded-lg hover:opacity-90 disabled:opacity-60">{sending ? <RefreshCw size={13} className="animate-spin" /> : <Mail size={13} />} Send packet</button>
        </div>
      </div>
    </div>
  );
}
